import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Textarea from '../../common/Textarea';
import Button from '../../common/Button';
import { validateRequired } from '../../../utils/validators';
import api from '../../../services/api';
import { API_ENDPOINTS } from '../../../constants/config';
import { IATA_CODES } from '../../../constants/iataCodes';
import branchService from '../../../services/branchAddressService';

const EditSiteModal = ({ isOpen, onClose, onSubmit, site }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    address: '',
    escalation_matrix_id: '',
    branch_address_id: '',
    company_name: '' // For display only
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [matrices, setMatrices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingMatrices, setLoadingMatrices] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      loadMatrices();
      loadBranchAddresses();
    }
  }, [isOpen]);

  const loadMatrices = async () => {
    setLoadingMatrices(true);
    try {
      const response = await api.get(API_ENDPOINTS.ESCALATION_MATRICES);
      setMatrices(response.data || []);
    } catch (error) {
      console.error("Failed to load matrices", error);
    } finally {
      setLoadingMatrices(false);
    }
  };

  // Loads active branch addresses for workreport 
  const loadBranchAddresses = async () => {
    setLoadingBranches(true);

    try {
      const result = await branchService.getAll();

      if (result.success) {
        setBranches(result.data || []);
      } else {
        alert(result.error || "Failed to load branch addresses");
      }
    } finally {
      setLoadingBranches(false);
    }};

  useEffect(() => {
    if (site && isOpen) {
      setFormData({
        name: site.name || '',
        location: site.location || '',
        address: site.address || '',
        escalation_matrix_id: site.escalation_matrix?.id ? site.escalation_matrix.id.toString() : '',
        branch_address_id: site.branch_address_id ? site.branch_address_id.toString() : '',
        company_name: site.company?.company_name || site.company_name || 'Unassigned'
      });
    }
  }, [site, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!validateRequired(formData.name)) newErrors.name = 'Site name is required';
    if (!validateRequired(formData.location)) newErrors.location = 'Location is required';
    if (!validateRequired(formData.address)) newErrors.address = 'Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const updateData = {
        name: formData.name,
        location: formData.location,
        address: formData.address,
        escalation_matrix_id: formData.escalation_matrix_id ? parseInt(formData.escalation_matrix_id) : null,
        branch_address_id: formData.branch_address_id ? parseInt(formData.branch_address_id) : null
      };

      await onSubmit(site.id, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating site:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setErrors({});
      onClose();
    }
  };

  const locationOptions = [
    ...IATA_CODES.map(code => ({ value: code, label: code }))
  ];

  const matrixOptions = [
    { value: '', label: '-- Select Escalation Matrix (Optional) --' },
    ...matrices.map(m => ({ value: m.id.toString(), label: m.name }))
  ];

  //If a branch is default label it clearly so admin knows which one
  // will be used auctomatically when no branch is mapped to a site
  const branchOptions = [
    ...branches.map(b => ({ value: b.id.toString(), label: b.is_default ? `${b.name} (Default)` : b.name}))
  ]

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Updating...' : 'Update Site'}
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit Site - ${site?.name}`} size="medium" footer={footer}>
      <form onSubmit={handleSubmit}>
        
        <div style={gridStyle}>
          <Input 
            label="Company" 
            name="company_name" 
            value={formData.company_name} 
            disabled 
          />
          <Select
            label="Location (IATA)"
            name="location"
            value={formData.location}
            onChange={handleChange}
            options={locationOptions}
            error={errors.location}
            required
          />
        </div>

        <div style={gridStyle}>
          <Input 
            label="Site Name" 
            name="name" 
            placeholder="e.g., Koramangala HQ" 
            value={formData.name} 
            onChange={handleChange} 
            error={errors.name} 
            required 
          />
          <Select
            label="Escalation Matrix"
            name="escalation_matrix_id"
            value={formData.escalation_matrix_id}
            onChange={handleChange}
            options={matrixOptions}
            disabled={loadingMatrices}
          />
        </div>
        
        <div style={gridStyle}>
          <Select
            label="Branch Address"
            name="branch_address_id"
            value={formData.branch_address_id}
            onChange={handleChange}
            options={branchOptions}
            disabled={loadingBranches}
            />
        </div>

        <div style={{ marginTop: '16px' }}>
           <Textarea
             label="Detailed Address"
             name="address"
             placeholder="Enter full address of the site..."
             value={formData.address}
             onChange={handleChange}
             error={errors.address}
             required
             rows={3}
           />
        </div>
      </form>
    </Modal>
  );
};

export default EditSiteModal;